const SIGNUP_LEAD_TOKEN_TTL_MS = 7 * 24 * 60 * 60 * 1000;

// El token del lead es un UUID. Validarlo ANTES de guardarlo evita que un `?token=` sucio
// —un `utm_source` mal armado en un anuncio, por ejemplo— quede una semana en el navegador
// ensuciando cada intento de registro.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Un código de prueba (`/signup?codigo=CYBER30`): letras, números, guion y guion bajo. Lo demás no se guarda.
const TRIAL_CODE_PATTERN = /^[A-Za-z0-9_-]{1,40}$/;

const TUNNEL_DRAFT_PREFIX = 'tunnel_draft_v1_';

// Los borradores del chat de soporte. Es la MISMA clave que `DRAFT_KEY_PREFIX` de
// `components/support_chat/SupportChatService.js`: si cambia allá, cambia acá (acá sólo se barre).
const SUPPORT_CHAT_DRAFT_PREFIX = 'support_chat_draft_';

// La marca que pone `/signup` en `sessionStorage` cuando el token del lead llegó de dashbo.io EN ESTA
// PESTAÑA. Es la MISMA clave que `FRESH_LEAD_KEY` de `components/agency/leadPrefill.js`: si cambia
// allá, cambia acá (acá sólo se lee).
const FRESH_LEAD_SESSION_KEY = 'signup_lead_fresh_token';

// Preferencias del Tablero (filtros y orden). Antes eran claves GLOBALES al navegador: una búsqueda
// hecha en una agencia aparecía en el Tablero de otra (card OGGNnRpB: «avis» de Bakián en la agencia
// nueva de Leo, con el Tablero vacío). Ahora llevan agencia y usuario.
const DASHBOARD_PREF_PREFIX = 'dashboard_pref_v1_';
// Las globales de antes. Se DESCARTAN, no se heredan: no hay forma de saber de qué agencia eran.
const LEGACY_DASHBOARD_PREF_KEYS = ['textFilter', 'issuesOnlyFilter', 'labelsFilter', 'platformFilter', 'columnOrdering'];

// Estado de las conexiones caídas, para el banner global. Es de la AGENCIA de la sesión: se guarda
// con su agencia y sólo se lee si coincide con la de la sesión actual (C-app-01).
const DISCONNECTED_CREDENTIALS_KEYS = [
    'logged_has_disconnected_credentials',
    'logged_disconnected_credentials_channels',
    'logged_disconnected_credentials_count',
    'logged_disconnected_credentials_agency_id',
    'logged_disconnected_credentials_dismissed_fp',
];

export class LocalStorage {
    static saveUserData(id, token, userType, agencyStatus, language, clientId = null, isDashboMcp = false, freeTrialDaysRemaining = null, agencyLogoUrl = null, agencyDisplayName = null, brandPrimaryColor = null, brandSecondaryColor = null, portalTheme = null) {
        // Entrar con OTRA cuenta sin haber salido: ni `/login` ni `/signup` redirigen a quien tiene
        // sesión, y esto pisaba usuario y token dejando todo lo demás de la sesión anterior — su
        // agencia, el banner de sus conexiones caídas, sus filtros (C-app-01). Se limpia como un
        // logout. El token del lead se queda SÓLO si llegó de dashbo.io en esta pestaña: es el de
        // esta entrada, el camino de la card (Bakián abierta, alta nueva desde dashbo.io). Uno que
        // no llegó acá puede ser de otra persona y viajaría como atribución en su alta (K-app-01).
        const previousUserId = localStorage.getItem('logged_user_id');
        if (previousUserId !== null && String(previousUserId) !== String(id)) {
            LocalStorage.clearSessionForAccountSwitch();
        }
        localStorage.setItem('logged_user_id', id);
        localStorage.setItem('logged_user_token', token);
        localStorage.setItem('logged_user_type', userType);
        localStorage.setItem('logged_agency_status', agencyStatus);
        localStorage.setItem('logged_user_language', language);
        localStorage.setItem('logged_is_dashbo_mcp', isDashboMcp ? 'true' : 'false');

        if (freeTrialDaysRemaining !== null && freeTrialDaysRemaining !== undefined) {
            localStorage.setItem('logged_free_trial_days_remaining', String(freeTrialDaysRemaining));
        } else {
            localStorage.removeItem('logged_free_trial_days_remaining');
        }

        if (clientId !== null) {
            localStorage.setItem('logged_user_client_id', clientId);
        } else {
            localStorage.removeItem('logged_user_client_id');
        }

        if (agencyLogoUrl) {
            localStorage.setItem('logged_agency_logo_url', agencyLogoUrl);
        } else {
            localStorage.removeItem('logged_agency_logo_url');
        }

        if (agencyDisplayName) {
            localStorage.setItem('logged_agency_display_name', agencyDisplayName);
        } else {
            localStorage.removeItem('logged_agency_display_name');
        }

        if (brandPrimaryColor) {
            localStorage.setItem('logged_brand_primary_color', brandPrimaryColor);
        } else {
            localStorage.removeItem('logged_brand_primary_color');
        }

        if (brandSecondaryColor) {
            localStorage.setItem('logged_brand_secondary_color', brandSecondaryColor);
        } else {
            localStorage.removeItem('logged_brand_secondary_color');
        }

        if (portalTheme) {
            localStorage.setItem('logged_portal_theme', portalTheme);
        } else {
            localStorage.removeItem('logged_portal_theme');
        }
    }

    static saveColumnWidth(columnId, width) {
        // Lee los anchos almacenados de localStorage, si existen
        const columnWidths = JSON.parse(localStorage.getItem('columnWidths')) || {};

        // Actualiza el ancho de la columna correspondiente
        columnWidths[columnId] = width;

        // Guarda de nuevo en localStorage
        localStorage.setItem('columnWidths', JSON.stringify(columnWidths));
    }

    // Token del lead que originó el registro: lo pone el sitio de marketing en
    // /signup?token=... y lo consume el alta de agencia para heredar de dónde vino esa
    // agencia (campaña, anuncio) y poder atribuir la conversión.
    //
    // Va en LocalStorage y no en el state porque el login con Google es un redirect de
    // página completa: cualquier estado en memoria se pierde en el camino.
    static saveSignupLeadToken(token) {
        // Se valida y se guarda el MISMO valor (el trimmeado). Validar uno y persistir otro
        // dejaba pasar al backend un token con espacios que acá había dado por válido.
        const cleanToken = String(token || '').trim();
        if (!UUID_PATTERN.test(cleanToken)) {
            return;
        }
        localStorage.setItem('signup_lead_token', JSON.stringify({token: cleanToken, savedAt: Date.now()}));
    }

    static getSignupLeadToken() {
        const stored = localStorage.getItem('signup_lead_token');
        if (!stored) {
            return null;
        }
        try {
            const parsed = JSON.parse(stored);
            // Sin `savedAt` numérico no hay TTL posible: `Date.now() - undefined` es `NaN`, y
            // `NaN > TTL` es `false`, así que el token no vencería NUNCA. Un JSON válido pero
            // con otra forma (de una versión anterior, o escrito a mano) entra por acá.
            if (!parsed || typeof parsed.savedAt !== 'number') {
                LocalStorage.removeSignupLeadToken();
                return null;
            }
            // Pasada la semana, quien está registrándose ya no es el que hizo aquel clic.
            // Se borra en vez de sólo ignorarlo: si no, el valor muerto queda para siempre.
            if ((Date.now() - parsed.savedAt) > SIGNUP_LEAD_TOKEN_TTL_MS) {
                LocalStorage.removeSignupLeadToken();
                return null;
            }
            return parsed.token || null;
        } catch (e) {
            // Contenido corrupto: sacarlo de en medio en vez de fallar en cada lectura.
            LocalStorage.removeSignupLeadToken();
            return null;
        }
    }

    static removeSignupLeadToken() {
        localStorage.removeItem('signup_lead_token');
    }

    // El código de prueba que llegó en /signup?codigo=... (CY-T, el Cyber 2026). Como el token del lead,
    // va en LocalStorage porque el login con Google es un redirect de página completa. Lo consume el alta
    // de agencia (lo aplica sola al crear la cuenta) y lo ofrece prellenado el formulario del código.
    static saveSignupTrialCode(code) {
        const cleanCode = String(code || '').trim();
        if (!TRIAL_CODE_PATTERN.test(cleanCode)) {
            return;
        }
        localStorage.setItem('signup_trial_code', JSON.stringify({code: cleanCode, savedAt: Date.now()}));
    }

    static getSignupTrialCode() {
        const stored = localStorage.getItem('signup_trial_code');
        if (!stored) {
            return null;
        }
        try {
            const parsed = JSON.parse(stored);
            if (!parsed || typeof parsed.savedAt !== 'number' || typeof parsed.code !== 'string'
                    || (Date.now() - parsed.savedAt) > SIGNUP_LEAD_TOKEN_TTL_MS) {
                LocalStorage.removeSignupTrialCode();
                return null;
            }
            return parsed.code;
        } catch (e) {
            LocalStorage.removeSignupTrialCode();
            return null;
        }
    }

    static removeSignupTrialCode() {
        localStorage.removeItem('signup_trial_code');
    }

    // El último canje de un código de prueba: `{days_remaining, at}`. Lo lee el chequeo periódico para no
    // pisar el estado nuevo con una respuesta vieja (caché por worker del backend, review C-cy-t-02).
    static saveRecentTrialRedemption(daysRemaining) {
        localStorage.setItem('trial_code_recent_redemption', JSON.stringify({days_remaining: daysRemaining, at: Date.now()}));
    }

    static fetchRecentTrialRedemption() {
        try {
            const parsed = JSON.parse(localStorage.getItem('trial_code_recent_redemption'));
            return parsed && Number.isInteger(parsed.days_remaining) && typeof parsed.at === 'number' ? parsed : null;
        } catch (e) {
            return null;
        }
    }

    static _leadTokenArrivedInThisTab() {
        const token = LocalStorage.getSignupLeadToken();
        try {
            return !!token && sessionStorage.getItem(FRESH_LEAD_SESSION_KEY) === token;
        } catch (e) {
            return false;
        }
    }

    // --- Borrador del túnel de alta (UX 2.1).
    //
    // Guarda SÓLO las decisiones del usuario, nunca la propuesta: el backend la recalcula en cada
    // consulta a propósito, y con 5.890 cuentas guardarla no entraría en la cuota. La clave lleva
    // agencia y usuario para que un borrador no cruce de cuenta ni de agencia.
    //
    // La validación de forma y el TTL viven en `components/onboarding/wizardStorage.js` (lógica
    // pura, testeable sin navegador); acá está sólo el I/O.
    static tunnelDraftKey(agencyId, userId) {
        return `${TUNNEL_DRAFT_PREFIX}${agencyId}_${userId}`;
    }

    static saveTunnelDraft(agencyId, userId, payload) {
        try {
            localStorage.setItem(LocalStorage.tunnelDraftKey(agencyId, userId), JSON.stringify(payload));
            return true;
        } catch (e) {
            // QuotaExceededError: se degrada a "sin persistencia" en vez de romper la pantalla.
            return false;
        }
    }

    static fetchTunnelDraft(agencyId, userId) {
        const stored = localStorage.getItem(LocalStorage.tunnelDraftKey(agencyId, userId));
        if (!stored) return null;
        try {
            return JSON.parse(stored);
        } catch (e) {
            // Contenido corrupto: sacarlo de en medio en vez de fallar en cada lectura.
            LocalStorage.removeTunnelDraft(agencyId, userId);
            return null;
        }
    }

    static removeTunnelDraft(agencyId, userId) {
        localStorage.removeItem(LocalStorage.tunnelDraftKey(agencyId, userId));
    }

    // Al desloguear se van TODOS los borradores, no sólo el de la sesión que cierra: no filtrar
    // entre cuentas pesa más que conservar un borrador.
    static removeEveryTunnelDraft() {
        const keys = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.indexOf(TUNNEL_DRAFT_PREFIX) === 0) keys.push(key);
        }
        keys.forEach((key) => localStorage.removeItem(key));
    }

    // --- Preferencias del Tablero, por agencia y usuario.
    //
    // Sin agencia o sin usuario conocidos no hay clave: la lectura da el default y la escritura no se
    // guarda. Pasa en la primera pantalla después del login, porque `logged_agency_id` lo hidrata un
    // fetch (`get-logged-user-data/`) que todavía no volvió. Perder un filtro en ese instante es
    // preferible a guardarlo en una clave que después lee otra agencia.
    static dashboardPrefKey(name) {
        const agencyId = LocalStorage.fetchAgencyId();
        const userId = LocalStorage.fetchUserId();
        if (!agencyId || !userId) return null;
        return `${DASHBOARD_PREF_PREFIX}${agencyId}_${userId}_${name}`;
    }

    static fetchDashboardPref(name) {
        const key = LocalStorage.dashboardPrefKey(name);
        return key ? localStorage.getItem(key) : null;
    }

    static saveDashboardPref(name, value) {
        const key = LocalStorage.dashboardPrefKey(name);
        if (!key) return;
        try {
            localStorage.setItem(key, value);
        } catch (e) {
            // QuotaExceededError: se pierde la preferencia, no el Tablero.
        }
    }

    static removeLegacyDashboardPrefs() {
        LEGACY_DASHBOARD_PREF_KEYS.forEach((key) => localStorage.removeItem(key));
    }

    // Al desloguear se van las de TODAS las agencias y usuarios, igual que los borradores del túnel.
    static removeEveryDashboardPref() {
        const keys = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.indexOf(DASHBOARD_PREF_PREFIX) === 0) keys.push(key);
        }
        keys.forEach((key) => localStorage.removeItem(key));
        LocalStorage.removeLegacyDashboardPrefs();
    }

    // Los borradores del chat de soporte, de cualquier usuario, también los de clave global de antes
    // del 15/10: la cuenta siguiente en el navegador veía el de la anterior (C-fserb-d0-02).
    static removeEverySupportChatDraft() {
        const keys = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.indexOf(SUPPORT_CHAT_DRAFT_PREFIX) === 0) keys.push(key);
        }
        keys.forEach((key) => localStorage.removeItem(key));
    }

    static hasUserData() {
        return localStorage.getItem('logged_user_id') !== null &&
            localStorage.getItem('logged_user_token') !== null &&
            localStorage.getItem('logged_user_type') !== null &&
            localStorage.getItem('logged_user_language') !== null &&
            (localStorage.getItem('logged_user_type') === 'AGENCY_USER' ||
                localStorage.getItem('logged_user_type') === 'CLIENT_USER' ||
                localStorage.getItem('logged_user_client_id') !== null);
    }

    static hasUserClientId() {
        return localStorage.getItem('logged_user_client_id') !== null;
    }

    // Logout: la sesión entera y, además, el token del lead. El token es de quien llegó desde la web;
    // quien entra después en el mismo navegador no lo hereda (C-registro-03, que pide esta mitad acá).
    static removeUserData() {
        LocalStorage.clearSessionData();
        LocalStorage.removeSignupLeadToken();
        LocalStorage.removeSignupTrialCode();
    }

    // Cambiar de cuenta: la sesión entera, como un logout, pero el token del lead se queda si llegó de
    // dashbo.io EN ESTA PESTAÑA. Lo usan la entrada con otra cuenta sin salir (`saveUserData`) y el
    // «Usar otra cuenta» del registro: quien vino de un anuncio y entró con la cuenta de Google
    // equivocada no pierde ni la atribución ni el prellenado al volver a entrar con la correcta.
    static clearSessionForAccountSwitch() {
        const keepLeadToken = LocalStorage._leadTokenArrivedInThisTab();
        LocalStorage.clearSessionData();
        if (!keepLeadToken) LocalStorage.removeSignupLeadToken();
    }

    // Todo lo que es de la SESIÓN (usuario, agencia y lo que depende de ellas). Lo usan el logout y
    // la entrada con otra cuenta sin salir (`saveUserData`), que conserva el token del lead.
    static clearSessionData() {
        localStorage.removeItem('logged_user_id');
        localStorage.removeItem('logged_user_token');
        localStorage.removeItem('logged_user_type');
        localStorage.removeItem('logged_user_language');
        localStorage.removeItem('logged_user_client_id');
        localStorage.removeItem('logged_agency_status');
        localStorage.removeItem('logged_agency_id');
        localStorage.removeItem('logged_is_dashbo_mcp');
        localStorage.removeItem('logged_is_super_admin');
        localStorage.removeItem('logged_free_trial_days_remaining');
        localStorage.removeItem('logged_trial_next_step');
        localStorage.removeItem('trial_code_recent_redemption');
        localStorage.removeItem('logged_has_payment_suspension_warning');
        localStorage.removeItem('logged_agency_logo_url');
        localStorage.removeItem('logged_agency_display_name');
        localStorage.removeItem('logged_brand_primary_color');
        localStorage.removeItem('logged_brand_secondary_color');
        localStorage.removeItem('logged_portal_theme');
        localStorage.removeItem('logged_user_clients');  // snapshot clients[] del portal multi-cliente
        localStorage.removeItem('logged_agency_report_modality');
        localStorage.removeItem('agency_custom_metrics_enabled');  // F8: flag de feature per-agency
        localStorage.removeItem('knews_unread_count');  // cache del bell de novedades (TTL 60 min)
        LocalStorage.removeEveryTunnelDraft();  // borradores del túnel de alta, de cualquier agencia
        LocalStorage.removeEveryDashboardPref();  // filtros y orden del Tablero, de cualquier agencia
        LocalStorage.removeEverySupportChatDraft();  // borradores sin enviar del chat de soporte
        // El banner de conexiones caídas se alimenta de estas claves y las relee cada 5 s: sin
        // borrarlas, `/login` mostraba las conexiones de la agencia que acababa de salir (OGGNnRpB).
        DISCONNECTED_CREDENTIALS_KEYS.forEach((key) => localStorage.removeItem(key));
        // La nota de "volver al túnel" después de autorizar una plataforma. Cerrar sesión no recarga
        // la página, así que sin esto sobrevive: el usuario siguiente conecta una cuenta por la
        // pantalla clásica y termina depositado en un recorrido que no empezó.
        sessionStorage.removeItem('ob_oauth_return');
        // Lo mismo con las dos notas del OAuth de Escalafy (`client/economia/state/escalafyModel.js`): la de a
        // dónde volver y la lista de tiendas del portal. Sin esto, quien entraba después en la misma pestaña
        // veía el modal «¿Qué tienda quieres compartir…?» con las tiendas del usuario anterior.
        sessionStorage.removeItem('pending_escalafy_connection');
        sessionStorage.removeItem('pending_escalafy_portal_pick');
        // Y la vuelta del OAuth de Mercado Ads (`channels/mercado_ads/mercadoAds.js`): el resultado del canje
        // (apodo del vendedor y anunciantes de ESA agencia) y el cliente del Portal que lo pidió.
        sessionStorage.removeItem('dashbo.mercado_ads.connect_result');
        sessionStorage.removeItem('dashbo.mercado_ads.portal_client');
    }

    static fetchUserId() {
        return localStorage.getItem('logged_user_id');
    }

    static fetchUserLanguage() {
        return localStorage.getItem('logged_user_language');
    }

    // El idioma que el usuario acaba de elegir (selector del portal). Es la clave que `i18n.js` lee
    // al arrancar, así que la página siguiente ya abre en ese idioma.
    static updateUserLanguage(language) {
        localStorage.setItem('logged_user_language', language);
    }

    static fetchUserToken() {
        return localStorage.getItem('logged_user_token');
    }

    static fetchUserType() {
        return localStorage.getItem('logged_user_type');
    }

    static fetchAgencyStatus() {
        return localStorage.getItem('logged_agency_status');
    }

    static updateAgencyStatus(agencyStatus) {
        localStorage.setItem('logged_agency_status', agencyStatus);
    }

    static fetchAgencyId() {
        return localStorage.getItem('logged_agency_id');
    }

    static updateAgencyId(agencyId) {
        localStorage.setItem('logged_agency_id', agencyId);
    }

    static fetchIsDashboMcp() {
        return localStorage.getItem('logged_is_dashbo_mcp') === 'true';
    }

    static updateIsDashboMcp(value) {
        localStorage.setItem('logged_is_dashbo_mcp', value ? 'true' : 'false');
    }

    // La modalidad se guarda JUNTO con la agencia: un cambio de agencia sin logout (switch de
    // super admin, login sin cerrar sesión) dejaba la modalidad de la agencia anterior como seed
    // válido (review 05/09, C-informes-15). Un valor de otra agencia no cuenta.
    static _readAgencyReportModality() {
        const raw = localStorage.getItem('logged_agency_report_modality');
        if (!raw) return null;
        try {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object' && parsed.m) return parsed;
        } catch (e) {
            // formato viejo (string pelado): no sabemos de qué agencia era
        }
        return null;
    }

    /** Hay un valor persistido DE ESTA agencia (a diferencia del default por ausencia). */
    static hasAgencyReportModality() {
        const stored = LocalStorage._readAgencyReportModality();
        const agencyId = LocalStorage.fetchAgencyId();
        return !!stored && !!agencyId && String(stored.a) === String(agencyId);
    }

    static fetchAgencyReportModality() {
        const stored = LocalStorage._readAgencyReportModality();
        if (!stored) return 'DATA_EXPLORER';
        const agencyId = LocalStorage.fetchAgencyId();
        if (agencyId && String(stored.a) !== String(agencyId)) return 'DATA_EXPLORER';
        return stored.m || 'DATA_EXPLORER';
    }

    static updateAgencyReportModality(value, agencyId = null) {
        const agency = agencyId != null ? String(agencyId) : LocalStorage.fetchAgencyId();
        if (value && agency) {
            localStorage.setItem('logged_agency_report_modality', JSON.stringify({a: agency, m: value}));
        } else {
            localStorage.removeItem('logged_agency_report_modality');
        }
    }

    static fetchIsSuperAdmin() {
        return localStorage.getItem('logged_is_super_admin') === 'true';
    }

    static updateIsSuperAdmin(value) {
        localStorage.setItem('logged_is_super_admin', value ? 'true' : 'false');
    }

    static fetchCustomMetricsEnabled() {
        // Hidratado desde basic-data.custom_metrics_enabled en cada carga del dashboard.
        // Devuelve true/false si está hidratado; null si todavía no corrió basic-data
        // (caso: navegación directa a /agency/custom-metrics post-login fresh).
        const value = localStorage.getItem('agency_custom_metrics_enabled');
        if (value === 'true') return true;
        if (value === 'false') return false;
        return null;
    }

    static updateCustomMetricsEnabled(enabled) {
        localStorage.setItem('agency_custom_metrics_enabled', enabled ? 'true' : 'false');
    }

    static fetchFreeTrialDaysRemaining() {
        const value = localStorage.getItem('logged_free_trial_days_remaining');
        if (value === null) return null;
        const parsed = parseInt(value, 10);
        return isNaN(parsed) ? null : parsed;
    }

    static updateFreeTrialDaysRemaining(days) {
        if (days !== null && days !== undefined) {
            localStorage.setItem('logged_free_trial_days_remaining', String(days));
        } else {
            localStorage.removeItem('logged_free_trial_days_remaining');
        }
    }

    // El próximo paso de la prueba (`trial_next_step` del backend: `{code, days_remaining}`), el mismo
    // que piden el correo y el WhatsApp del seguimiento. Lo lee el banner de la prueba. Sin paso (una
    // agencia paga, o un backend que todavía no lo manda) devuelve null.
    static fetchTrialNextStep() {
        const value = localStorage.getItem('logged_trial_next_step');
        if (!value) return null;
        try {
            const parsed = JSON.parse(value);
            return parsed && typeof parsed.code === 'string' ? parsed : null;
        } catch (e) {
            return null;
        }
    }

    static updateTrialNextStep(step) {
        if (step && typeof step.code === 'string') {
            localStorage.setItem('logged_trial_next_step', JSON.stringify(step));
        } else {
            localStorage.removeItem('logged_trial_next_step');
        }
    }

    static fetchHasPaymentSuspensionWarning() {
        return localStorage.getItem('logged_has_payment_suspension_warning') === 'true';
    }

    static updateHasPaymentSuspensionWarning(value) {
        if (value === true) {
            localStorage.setItem('logged_has_payment_suspension_warning', 'true');
        } else {
            localStorage.removeItem('logged_has_payment_suspension_warning');
        }
    }

    // ¿El estado guardado de las conexiones caídas es de la agencia de ESTA sesión? Lo escribe el
    // chequeo periódico con la agencia que lo pidió. Sin agencia conocida, o con la de otra sesión
    // (entrar sin salir, o una respuesta de la sesión anterior que llegó tarde), no hay banner.
    static _disconnectedCredentialsAreOfThisAgency() {
        const agencyId = LocalStorage.fetchAgencyId();
        return !!agencyId &&
            localStorage.getItem('logged_disconnected_credentials_agency_id') === String(agencyId);
    }

    static fetchHasDisconnectedCredentials() {
        return LocalStorage._disconnectedCredentialsAreOfThisAgency() &&
            localStorage.getItem('logged_has_disconnected_credentials') === 'true';
    }

    static fetchDisconnectedCredentialsChannels() {
        if (!LocalStorage._disconnectedCredentialsAreOfThisAgency()) return [];
        const raw = localStorage.getItem('logged_disconnected_credentials_channels');
        if (!raw) return [];
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch (_) {
            return [];
        }
    }

    // Cuántos ACCESOS hay caídos (no canales: dos accesos de Google caídos son dos). Lo leen el
    // desplegable de la agencia y el punto rojo de Conexiones en la 2.1 («N por reconectar»). Un
    // estado guardado sin el conteo —escrito por un bundle anterior— cuenta al menos uno por canal.
    static fetchDisconnectedCredentialsCount() {
        if (!LocalStorage.fetchHasDisconnectedCredentials()) return 0;
        const parsed = parseInt(localStorage.getItem('logged_disconnected_credentials_count'), 10);
        if (parsed > 0) return parsed;
        return Math.max(1, LocalStorage.fetchDisconnectedCredentialsChannels().length);
    }

    // Escribe el resultado del chequeo JUNTO con la agencia a la que corresponde. `count` es el
    // `count` del mismo resumen (`agency/disconnected-credentials-summary/`): opcional, para no
    // cambiar a quien no lo pasa.
    static updateDisconnectedCredentials(agencyId, hasDisconnected, channels, count) {
        if (!agencyId || hasDisconnected !== true) {
            localStorage.removeItem('logged_has_disconnected_credentials');
            localStorage.removeItem('logged_disconnected_credentials_channels');
            localStorage.removeItem('logged_disconnected_credentials_count');
            localStorage.removeItem('logged_disconnected_credentials_agency_id');
            return;
        }
        localStorage.setItem('logged_has_disconnected_credentials', 'true');
        localStorage.setItem('logged_disconnected_credentials_agency_id', String(agencyId));
        if (Array.isArray(channels) && channels.length > 0) {
            localStorage.setItem('logged_disconnected_credentials_channels', JSON.stringify(channels));
        } else {
            localStorage.removeItem('logged_disconnected_credentials_channels');
        }
        if (Number.isInteger(count) && count > 0) {
            localStorage.setItem('logged_disconnected_credentials_count', String(count));
        } else {
            localStorage.removeItem('logged_disconnected_credentials_count');
        }
    }

    // Fingerprint del set de canales desconectados que el user marcó como
    // dismiss. Si el fingerprint actual no coincide con el guardado, el banner
    // vuelve a aparecer (porque cambió el set — se desconectó algo nuevo).
    // Va con la agencia: el «ocultar» de LinkedIn en una agencia no oculta el de otra.
    static fetchDismissedDisconnectedFingerprint() {
        const stored = localStorage.getItem('logged_disconnected_credentials_dismissed_fp');
        const agencyId = LocalStorage.fetchAgencyId();
        const prefix = `${agencyId}:`;
        if (!stored || !agencyId || stored.indexOf(prefix) !== 0) return null;
        return stored.slice(prefix.length) || null;
    }

    static updateDismissedDisconnectedFingerprint(fingerprint) {
        const agencyId = LocalStorage.fetchAgencyId();
        if (fingerprint && agencyId) {
            localStorage.setItem('logged_disconnected_credentials_dismissed_fp', `${agencyId}:${fingerprint}`);
        } else {
            localStorage.removeItem('logged_disconnected_credentials_dismissed_fp');
        }
    }

    static fetchUserClientId() {
        return localStorage.getItem('logged_user_client_id');
    }

    // --- Portal multi-cliente ---
    // `logged_user_clients` es el snapshot de `clients[]` del login (branding + features +
    // modules por cliente); lo hidrata también el refresh de PortalClientProvider.
    static saveUserClients(clients) {
        if (Array.isArray(clients) && clients.length > 0) {
            localStorage.setItem('logged_user_clients', JSON.stringify(clients));
        } else {
            localStorage.removeItem('logged_user_clients');
        }
    }

    static fetchUserClients() {
        const raw = localStorage.getItem('logged_user_clients');
        if (!raw) return null;
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
        } catch (_) {
            return null;
        }
    }

    // El cliente activo se persiste keyed por usuario: una compu compartida o un re-login
    // con otra cuenta no hereda la selección. sessionStorage primero (cada pestaña puede
    // mirar un cliente distinto); localStorage recuerda el último usado entre sesiones y
    // sobrevive al logout a propósito (removeUserData no lo limpia).
    static saveActivePortalClient(userId, clientId) {
        if (!userId) return;
        const key = `portal_active_client_${userId}`;
        if (clientId) {
            localStorage.setItem(key, String(clientId));
            sessionStorage.setItem(key, String(clientId));
        } else {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
        }
    }

    static fetchActivePortalClient(userId) {
        if (!userId) return null;
        const key = `portal_active_client_${userId}`;
        return sessionStorage.getItem(key) || localStorage.getItem(key);
    }

    static isClientUser() {
        return localStorage.getItem('logged_user_type') === 'CLIENT_USER';
    }

    static fetchAgencyLogoUrl() {
        return localStorage.getItem('logged_agency_logo_url');
    }

    static fetchAgencyDisplayName() {
        return localStorage.getItem('logged_agency_display_name');
    }

    static fetchBrandPrimaryColor() {
        return localStorage.getItem('logged_brand_primary_color');
    }

    static fetchBrandSecondaryColor() {
        return localStorage.getItem('logged_brand_secondary_color');
    }

    // Template de diseño del portal del cliente (EDITORIAL / MINIMAL / IMMERSIVE).
    // Se hidrata en el login del CLIENT_USER (portal_theme de user_session_data).
    static fetchPortalTheme() {
        return localStorage.getItem('logged_portal_theme');
    }

    static updatePortalTheme(value) {
        if (value) {
            localStorage.setItem('logged_portal_theme', value);
        } else {
            localStorage.removeItem('logged_portal_theme');
        }
    }

    static saveDataExplorerSearchParams(searchParams) {
        const dataToSave = {
            ...searchParams,
            timestamp: Date.now(),
        };
        localStorage.setItem('data_explorer_last_search', JSON.stringify(dataToSave));
    }

    // Saved Views — persistir la vista cargada actual para que el banner sobreviva al F5/refresh.
    static saveLastLoadedViewId(viewId) {
        if (viewId === null || viewId === undefined) {
            localStorage.removeItem('data_explorer_last_view_id');
        } else {
            localStorage.setItem('data_explorer_last_view_id', String(viewId));
        }
    }

    static fetchLastLoadedViewId() {
        const raw = localStorage.getItem('data_explorer_last_view_id');
        if (!raw) return null;
        const parsed = parseInt(raw, 10);
        return Number.isFinite(parsed) ? parsed : null;
    }

    static clearLastLoadedViewId() {
        localStorage.removeItem('data_explorer_last_view_id');
    }

    static getDataExplorerSearchParams() {
        const savedData = localStorage.getItem('data_explorer_last_search');
        if (!savedData) {
            return null;
        }
        
        try {
            const parsed = JSON.parse(savedData);
            return parsed;
        } catch (error) {
            console.error('Error parsing saved search params:', error);
            return null;
        }
    }

    static shouldAutoExecuteSearch() {
        const savedData = this.getDataExplorerSearchParams();
        if (!savedData || !savedData.timestamp) {
            return false;
        }
        
        const twoHoursInMs = 2 * 60 * 60 * 1000; // 2 horas en milisegundos
        const timeSinceLastSearch = Date.now() - savedData.timestamp;
        
        return timeSinceLastSearch < twoHoursInMs;
    }

    static saveHeatMapEnabled(enabled) {
        localStorage.setItem('data_explorer_heat_map_enabled', JSON.stringify(enabled));
    }

    static getHeatMapEnabled() {
        const saved = localStorage.getItem('data_explorer_heat_map_enabled');
        if (saved === null) {
            // Por defecto es true
            return true;
        }
        try {
            return JSON.parse(saved);
        } catch (error) {
            console.error('Error parsing heat map enabled:', error);
            return true; // Por defecto es true
        }
    }

    static saveDataExplorerChartPreferences(prefs) {
        localStorage.setItem('data_explorer_chart_prefs', JSON.stringify(prefs));
    }

    static getDataExplorerChartPreferences() {
        const saved = localStorage.getItem('data_explorer_chart_prefs');
        if (!saved) return null;
        try {
            return JSON.parse(saved);
        } catch (error) {
            return null;
        }
    }

    static saveRedirectUrl(url) {
        localStorage.setItem('redirect_after_login', url);
    }

    static getRedirectUrl() {
        return localStorage.getItem('redirect_after_login');
    }

    static clearRedirectUrl() {
        localStorage.removeItem('redirect_after_login');
    }

    // Knews — cache del unread count del bell de novedades. Persiste {count, timestamp}
    // para que el TTL de 60 min sobreviva al F5/navegación y evite pegarle al endpoint
    // /knews/unread-count/ en cada focus de ventana o remount. La política de TTL vive
    // en el componente (KnewsBell); acá solo guardamos/leemos el blob.
    static saveKnewsUnreadCount(count) {
        localStorage.setItem('knews_unread_count', JSON.stringify({count, timestamp: Date.now()}));
    }

    static getKnewsUnreadCount() {
        const raw = localStorage.getItem('knews_unread_count');
        if (!raw) return null;
        try {
            const parsed = JSON.parse(raw);
            if (typeof parsed.count !== 'number' || typeof parsed.timestamp !== 'number') {
                return null;
            }
            return parsed;
        } catch (_) {
            return null;
        }
    }
}
